import {IDeepLinkHandler} from "../deep-link-handler.interface";
import {IServiceFactory} from "../../service-factory.interface";
import {Check} from "../../../types/type-checking";
import {NullableString} from "../../../types/nullable-types";
import {WidgetPaymentsRevolutPayOptions} from "@revolut/checkout";


export enum RevolutPayCallbackType {
    Success = 0,
    Cancel = 1,
    Failure = 2
}


export type RevolutPayDeepLinkListener = (callbackType: RevolutPayCallbackType) => Promise<void>;

export interface IRevolutPayDeepLinkListenerSubscription {
    unsubscribe(): void;
}

const CALLBACKS_BASE_URL = "aeroitalia://revolut-pay";
const CALLBACKS_BASE_URL_REVOLUT_APP_INSTALLED = `${CALLBACKS_BASE_URL}/installed`;
const CALLBACKS_BASE_URL_REVOLUT_APP_NOT_INSTALLED = `${CALLBACKS_BASE_URL}/not-installed`;
const CALLBACK_TYPE_QUERY_PARAM_NAME = "type";

export class RevolutPayDeepLinkHandler implements IDeepLinkHandler {
    constructor(private readonly service: IServiceFactory) {

    }

    canHandler(link: string): boolean {
        return link?.toLowerCase().startsWith(CALLBACKS_BASE_URL);
    }

    async execute(link: string): Promise<void> {
        if(link.toLowerCase().startsWith(CALLBACKS_BASE_URL_REVOLUT_APP_INSTALLED)) {
            await this._handleDeepLinkWhenRevolutAppInstalled(link);
        } else if(link.toLowerCase().startsWith(CALLBACKS_BASE_URL_REVOLUT_APP_NOT_INSTALLED)) {
            await this._handleDeepLinkWhenRevolutAppNotInstalled(link);
        }
    }

    async _handleDeepLinkWhenRevolutAppInstalled(link: string): Promise<void> {
        const url = new URL(link);
        let callbackType: RevolutPayCallbackType | null = null;
        if(url.searchParams.get("_rp_dos")) {
            callbackType = RevolutPayCallbackType.Cancel;
        } else if(url.searchParams.get("_rp_fr")) {
            callbackType = RevolutPayCallbackType.Failure;
        } else if(url.searchParams.get("_rp_s") === 'true') {
            callbackType = RevolutPayCallbackType.Success;
        }

        if(Check.isNullOrUndefined(callbackType)) {
            return;
        }

        for(let listener of this._deepLinkListeners) {
            try {
                await listener(callbackType);
            } catch(err) {
                this.service.logger.error(`Revolut deep link listener failed! listener: ${listener}`, err);
            }
        }
    }

    private async _handleDeepLinkWhenRevolutAppNotInstalled(link: string): Promise<void> {
        const url = new URL(link);
        const typeParamValue = url.searchParams.get(CALLBACK_TYPE_QUERY_PARAM_NAME);
        if(Check.isNull(typeParamValue)) {
            return;
        }
        const callbackType: RevolutPayCallbackType =  parseInt(typeParamValue);

        for(let listener of this._deepLinkListeners) {
            try {
                await listener(callbackType);
            } catch(err) {
                this.service.logger.error(`Revolut deep link listener failed! listener: ${listener}`, err);
            }

        }
    }

    private _deepLinkListeners: RevolutPayDeepLinkListener[] = [];

    registerListener(listener: RevolutPayDeepLinkListener): IRevolutPayDeepLinkListenerSubscription {
        this._deepLinkListeners.push(listener);

        return {
            unsubscribe: () => {
                const index = this._deepLinkListeners.indexOf(listener);
                if(index >= 0) {
                    this._deepLinkListeners.splice(index, 1);
                }
            }
        }

    }

    getAppReturnUrl():  NullableString {
        return CALLBACKS_BASE_URL_REVOLUT_APP_INSTALLED;
    }

    getMobileRedirectUrls():  Pick<WidgetPaymentsRevolutPayOptions, "mobileRedirectUrls"> {
        if(!this.service.device.isHybrid) {
            return {};
        }

        return {
            mobileRedirectUrls: {
                success: this._createRevolutPayHybridCallback(RevolutPayCallbackType.Success),
                cancel: this._createRevolutPayHybridCallback(RevolutPayCallbackType.Cancel),
                failure: this._createRevolutPayHybridCallback(RevolutPayCallbackType.Failure)
            }
        };
    }

    private _createRevolutPayHybridCallback(callbackType: RevolutPayCallbackType): string {
        return `${CALLBACKS_BASE_URL_REVOLUT_APP_NOT_INSTALLED}?${CALLBACK_TYPE_QUERY_PARAM_NAME}=${callbackType}`;
    }

}